import { inject, Injectable, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { ContextService, TypologyLiteral } from '@bcef/ia-shared';
import { WA_WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';

import { RoutesPath } from '@features/termination/enums';
import {
  CustomWindowResilContrats,
  PersonalInformation,
  TerminationInformation,
} from '@features/termination/interfaces';

import { ConnectionContextService } from './services';
import * as fromStore from './store';

@Injectable()
export class TerminationFacade {
  private readonly contextService = inject(ContextService);

  private readonly windowRef = inject<CustomWindowResilContrats>(WA_WINDOW);

  private readonly store = inject(Store);

  private readonly translate = inject(TranslateService);

  private readonly connectionContextService = inject(ConnectionContextService);

  private readonly router: Router = inject(Router);

  public readonly isMbGroup: boolean = this.contextService.isMbGroup;

  public readonly isPro: boolean = this.contextService.isPro;

  public readonly isHbGroup: boolean = this.contextService.isHbGroup;

  public get typology(): TypologyLiteral {
    return this.contextService.typology;
  }

  public customerInformationIsLoading$: Observable<boolean> = this.store.select(
    fromStore.selectCustomerInformationIsLoading,
  );

  public stepInProcess: Signal<number> = this.store.selectSignal(fromStore.selectStepInProcess);

  public personalInformation$: Observable<PersonalInformation> = this.store.select(
    fromStore.selectPersonalInformation,
  );

  public terminationInformation$: Observable<TerminationInformation> = this.store.select(
    fromStore.selectTerminationInformation,
  );

  public terminationInformationText$: Observable<TerminationInformation> = this.store.select(
    fromStore.selectTerminationInformationText,
  );

  public getCustomerInformation(): void {
    if (this.connectionContextService.isConnected) {
      this.store.dispatch(fromStore.CustomerInformationLoad());
    } else {
      this.navigateTo(RoutesPath.PERSONAL_PAGE);
    }
  }

  public navigateTo(path: RoutesPath): void {
    this.router.navigate([path], { skipLocationChange: true });
  }

  public updateStepInProgress(step: number): void {
    this.store.dispatch(fromStore.UpdateStep({ step: step }));
  }

  public closeWebView(): void {
    this.store.dispatch(fromStore.CloseWebView());
  }

  public openContact(): void {
    if (this.contextService.isMobile) {
      this.openContactWebView();
    } else {
      this.translate
        .get('url.urlContact')
        .pipe(take(1))
        .subscribe((url: string) => {
          this.windowRef.open(url, '_blank');
        });
    }
  }

  private openContactWebView(): void {
    this.store.dispatch(fromStore.OpenContactWebView());
  }
}
